import React, { useMemo } from "react";

import { faBadgeCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fallback, Root } from "@radix-ui/react-avatar";
import cx from "classnames";

import Avatar, { type AvatarProps, type AvatarUserAttrs } from "~/ui/avatar";
import Tooltip, { type TooltipProps, useSingleton } from "~/ui/Tooltip";

import useSplitUsersIntoOthers from "./useSplitUsersIntoOthers";

import avatarStyles from "./Avatar.module.css";
import styles from "./AvatarList.module.css";

export type AvatarListUser = AvatarUserAttrs &
	Pick<AvatarProps, "outline" | "grayscale"> & {
		id: string;
	};

function UserDetails({ user, isSelf }: { user: AvatarListUser; isSelf: boolean }) {
	return (
		<div className={styles.userDetails}>
			<p>
				<strong>
					{user.fullName}
					{isSelf && " (Me)"}
				</strong>
			</p>
			{/*
					Anonymous users will be external when coming from note stream invites.
					In that case we kinda know who they are so we don't show them as anonymous
				*/}
			{user.isAnonymous && !user.isExternal && <p>Anonymous viewer</p>}
			{user.isExternal && <p>From another workspace</p>}
			{user.isGuest && <p>Guest viewer</p>}
			{user.isTopUser && (
				<p>
					<FontAwesomeIcon icon={faBadgeCheck} /> Top Fellow User
				</p>
			)}
		</div>
	);
}

interface AvatarListProps extends Pick<AvatarProps, "size" | "hideIcon"> {
	className?: string;
	users: readonly AvatarListUser[];
	/** The ID of the user to refer to as "yourself". */
	selfId?: string;
	/** Excludes the "others" count. i.e. if `maxLength={2}` and there 5 users: Avatar1, Avatar2, +3 */
	maxLength?: number;
	/** Whether to omit the self user from the list. Make sure to provide the correct `selfId` if this is `true`. */
	omitSelf?: boolean;
	hideDetailsTooltip?: boolean;
	isDetailsTooltipInteractive?: boolean;
	detailsTooltipPlacement?: TooltipProps["placement"];
}

export default React.forwardRef<HTMLDivElement, AvatarListProps>(function AvatarList(
	{
		className,
		users,
		selfId = "",
		maxLength = 5,
		omitSelf = false,
		hideDetailsTooltip = false,
		size,
		hideIcon,
		isDetailsTooltipInteractive = true,
		detailsTooltipPlacement = "auto",
	},
	ref,
) {
	const [source, target] = useSingleton();

	const { usersToShow, hiddenUsers } = useSplitUsersIntoOthers(users, selfId, maxLength, omitSelf);

	// In order to have the first user appear on top of the "stack", we make it the last child relative to the other users.
	// This is then undone with a flex-direction: row-reverse in CSS.
	const usersForMapping = useMemo(() => Array.from(usersToShow).reverse(), [usersToShow]);

	return (
		<div ref={ref} className={cx(styles.avatarList, className)}>
			{/* For some reason `disabled={hideDetailsTooltip}` doesn't work when it's a singleton 🤔 */}
			{!hideDetailsTooltip && (
				<Tooltip
					interactive={isDetailsTooltipInteractive}
					singleton={source}
					delay={[250, 0]}
					placement={detailsTooltipPlacement}
				/>
			)}
			{hiddenUsers.length > 0 && (
				<OthersAvatar singleton={target} users={hiddenUsers} size={size} hideIcon={hideIcon} selfId={selfId} />
			)}
			{usersForMapping.map(user => (
				<Tooltip key={user.id} singleton={target} content={<UserDetails user={user} isSelf={user.id === selfId} />}>
					<Avatar
						user={user}
						className={styles.avatar}
						size={size}
						hideIcon={hideIcon}
						outline={user.outline}
						grayscale={user.grayscale}
					/>
				</Tooltip>
			))}
		</div>
	);
});

export function OthersList({
	users,
	hideIcon = false,
	selfId,
}: Pick<AvatarProps, "hideIcon"> & { users: readonly AvatarListUser[]; selfId: string }) {
	// NOTE: There's no height limit… Let's hope it's never too big?
	return (
		<div>
			{users.map(user => (
				<div key={user.id} className={styles.otherUserListItem}>
					<Avatar
						user={user}
						className={styles.avatar}
						size="small"
						hideIcon={hideIcon}
						outline={user.outline}
						grayscale={user.grayscale}
					/>
					<span>
						{user.fullName}
						{user.id === selfId && " (Me)"}
					</span>
				</div>
			))}
		</div>
	);
}

function OthersAvatar({
	users,
	singleton,
	size = "medium",
	hideIcon,
	selfId,
}: Pick<AvatarProps, "size" | "hideIcon"> & {
	users: AvatarListUser[];
	selfId: string;
	singleton: ReturnType<typeof useSingleton>[1];
}) {
	if (users.length === 0) return null;

	if (users.length === 1) {
		return (
			<Avatar
				user={users[0]}
				size={size}
				hideIcon={hideIcon}
				className={styles.avatar}
				outline={users[0].outline}
				grayscale={users[0].grayscale}
			/>
		);
	}

	return (
		<Tooltip singleton={singleton} content={<OthersList users={users} hideIcon={hideIcon} selfId={selfId} />}>
			<Root className={cx(avatarStyles.root, styles.avatar, avatarStyles[size])}>
				<Fallback asChild className={cx(avatarStyles.fallback, styles.othersCount, styles[size])}>
					<span className={cx({ [styles.doubleDigit]: users.length > 9 })}>+{Math.min(users.length, 99)}</span>
				</Fallback>
			</Root>
		</Tooltip>
	);
}
